import { ClockCircleOutlined, CloseOutlined } from '@ant-design/icons'
import {
  Add,
  Button,
  Edit,
  FormCard,
  FormItem,
  Input,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import {
  Form as AntdForm,
  Select as AntdSelect,
  DatePicker,
  Dropdown,
  InputNumber,
  TimePicker
} from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  OverrideLevel,
  type PaymentCard,
  type PaymentCardTeamMember,
  RateType
} from '../../hooks/usePaymentCards'
import { useShiftTypesList } from '../../hooks/useShiftTypesList'
import { useVisitTypesList } from '../../hooks/useVisitTypesList'
import { getIsDifferentRule, getRequiredRule } from '../../utils'
import {
  DATE_FORMAT,
  disabledPastDate,
  getDisabledPastTime,
  TIME_FORMAT
} from '../../utils.datetime'
import PayRateCardSummary, { type RateSummaryItem } from './PayRateCardSummary'
import PayRateHistoryDrawer from './PayRateHistoryDrawer'

const getRateTypeOptions = (t: (key: string) => string) => [
  { value: RateType.HOURLY, label: t('RATE_TYPE_HOURLY') },
  { value: RateType.EVENT, label: t('RATE_TYPE_FIXED') }
]

export const formatRateDisplay = (
  rate: number | string,
  rateType: RateType | undefined,
  t: (key: string) => string
) => {
  const value = typeof rate === 'string' ? parseFloat(rate) : rate
  return rateType === RateType.EVENT
    ? `${value} ${t('RATE_TYPE_FIXED')}`
    : `${value} ${t('RATE_TYPE_HOURLY')}`
}

export type PayRateCardProps = {
  fieldName: number
  form: ReturnType<typeof AntdForm.useForm>[0]
  onDirty: () => void
  onClean: () => void
  originalCard?: any
}

const buildPreviousVersionSummary = (
  version: PaymentCard,
  eventTypeNameMap: Record<string, string>
): { effectiveFrom: string; baseRate: any; overrides: RateSummaryItem[] } => {
  const baseRate = version.rates.find((r) => r.level === OverrideLevel.BASE)
  const overrides = version.rates
    .filter((r) => r.level !== OverrideLevel.BASE)
    .map((r) => ({
      name: eventTypeNameMap[r.eventTypeId!] || r.eventTypeId!,
      rate: r.rate,
      rateType: r.rateType
    }))
  const effectiveFrom = version.effectiveFrom
    ? `${dayjs(version.effectiveFrom).format(DATE_FORMAT)}  ${dayjs(version.effectiveFrom).format(TIME_FORMAT)}`
    : '-'

  return { effectiveFrom, baseRate: baseRate ?? null, overrides }
}

const buildFormValuesSummary = (
  effectiveDate: any,
  effectiveTime: any,
  rates: any[],
  eventTypeNameMap: Record<string, string>
): { effectiveFrom: string; baseRate: any; overrides: RateSummaryItem[] } => {
  const effectiveFrom =
    effectiveDate && effectiveTime
      ? `${dayjs(effectiveDate).format(DATE_FORMAT)}  ${dayjs(effectiveTime).format(TIME_FORMAT)}`
      : '-'
  const baseRate = rates[0]
    ? { rate: rates[0].rate, rateType: rates[0].rateType }
    : null
  const overrides = rates
    .filter((_: any, i: number) => i > 0)
    .filter((r: any) => !r?.isDeleted)
    .map((r: any) => ({
      name: eventTypeNameMap[r.eventTypeId] || r.eventTypeId,
      rate: r.rate,
      rateType: r.rateType
    }))

  return { effectiveFrom, baseRate, overrides }
}

const PayRateCard = ({
  fieldName,
  form,
  onDirty,
  onClean,
  originalCard
}: PayRateCardProps) => {
  const { t } = useTranslation()
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()
  const rateTypeOptions = getRateTypeOptions(t)

  const cardId = form.getFieldValue(['payRates', fieldName, '_id'])
  const isNewCard = !cardId
  const [isEditing, setIsEditing] = useState(isNewCard)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const initialId = form.getFieldValue(['payRates', fieldName, 'initialId'])
  const hasHistory = !!initialId && initialId !== cardId

  const shiftTypeIds = useMemo(
    () =>
      new Set(
        (shiftTypes || [])
          .filter((shiftType) => shiftType.id)
          .map((shiftType) => shiftType.id!)
      ),
    [shiftTypes]
  )

  const allEventTypes = useMemo(
    () => [
      ...(shiftTypes || [])
        .filter((shiftType) => shiftType.id && !shiftType.isDeleted)
        .map((shiftType) => ({ id: shiftType.id!, name: shiftType.name })),
      ...(visitTypes || [])
        .filter((visitType) => visitType.id && !visitType.isDeleted)
        .map((visitType) => ({ id: visitType.id!, name: visitType.name }))
    ],
    [shiftTypes, visitTypes]
  )

  const eventTypeOptions = useMemo(
    () =>
      allEventTypes.map((eventType) => ({
        value: eventType.id,
        label: <Text>{eventType.name}</Text>
      })),
    [allEventTypes]
  )

  const eventTypeNameMap = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        allEventTypes.map((eventType) => [eventType.id, eventType.name])
      ),
    [allEventTypes]
  )

  const name = AntdForm.useWatch(['payRates', fieldName, 'name'], form)
  const cardTitle = name || `${t('PAY_RATES')} ${fieldName + 1}`

  const currentRates =
    form.getFieldValue(['payRates', fieldName, 'rates']) || []
  const usedEventTypeIds = currentRates
    .filter((_rate: any, index: number) => index > 0 && !_rate?.isDeleted)
    .map((rate: any) => rate?.eventTypeId)
    .filter(Boolean)

  const availableOptions = eventTypeOptions.filter(
    (option) => !usedEventTypeIds.includes(option.value)
  )

  const previousVersion: PaymentCard | undefined = form.getFieldValue([
    'payRates',
    fieldName,
    'previousVersion'
  ])

  const getIsNameDifferentRule = () => {
    const formValues = form.getFieldsValue(true)
    const currentFormValues = (formValues.payRates || []).map((payRate: any) =>
      payRate?.isDeleted ? { ...payRate, name: undefined } : payRate
    )
    return getIsDifferentRule(currentFormValues, fieldName)
  }

  const handleCancel = () => {
    const allPayRates = form.getFieldsValue(true).payRates
    allPayRates[fieldName] = { ...originalCard }
    form.setFieldsValue({ payRates: allPayRates })
    setIsEditing(false)
    onClean()
  }

  const handleDelete = () => {
    onDirty()
    form.setFieldValue(['payRates', fieldName, 'isDeleted'], true)
  }

  const handleDeleteRate = (rateIndex: number) => {
    onDirty()
    form.setFieldValue(
      ['payRates', fieldName, 'rates', rateIndex, 'isDeleted'],
      true
    )
  }

  const handleStartEdit = () => {
    if (cardId && !previousVersion) {
      form.setFieldValue(['payRates', fieldName, 'effectiveDate'], undefined)
      form.setFieldValue(['payRates', fieldName, 'effectiveTime'], undefined)
      onDirty()
    }
    setIsEditing(true)
  }

  const filterDeletedRates = (rateIndex: number) => {
    const isDeleted = form.getFieldValue([
      'payRates',
      fieldName,
      'rates',
      rateIndex,
      'isDeleted'
    ])
    return isDeleted !== true
  }

  const prevSummary = previousVersion
    ? buildPreviousVersionSummary(previousVersion, eventTypeNameMap)
    : null

  const currentSummary = buildFormValuesSummary(
    form.getFieldValue(['payRates', fieldName, 'effectiveDate']),
    form.getFieldValue(['payRates', fieldName, 'effectiveTime']),
    currentRates,
    eventTypeNameMap
  )

  const originalSummary =
    !previousVersion && cardId && originalCard
      ? buildFormValuesSummary(
          originalCard.effectiveDate,
          originalCard.effectiveTime,
          originalCard.rates || [],
          eventTypeNameMap
        )
      : null

  if (!isEditing) {
    const teamMembers: PaymentCardTeamMember[] =
      form.getFieldValue(['payRates', fieldName, 'teamMembers']) || []

    return (
      <ViewFormCard
        level={2}
        title={previousVersion ? previousVersion.name : cardTitle}
        rightChildren={
          <Button
            variant="text"
            type="button"
            icon={<Edit />}
            onClick={handleStartEdit}
          >
            {previousVersion ? t('EDIT_PLANNED_RATE') : t('EDIT_RATE')}
          </Button>
        }
      >
        {prevSummary && (
          <>
            <PayRateCardSummary
              effectiveFrom={prevSummary.effectiveFrom}
              baseRate={prevSummary.baseRate}
              overrides={prevSummary.overrides}
              teamMembers={previousVersion?.teamMembers}
              showDividerAfter
            />
            <PlannedUpdateHeading level={4}>
              {t('PLANNED_RATE_UPDATE')}
            </PlannedUpdateHeading>
          </>
        )}

        <PayRateCardSummary
          effectiveFrom={currentSummary.effectiveFrom}
          baseRate={currentSummary.baseRate}
          overrides={currentSummary.overrides}
          teamMembers={teamMembers}
          showTeamMembers={!previousVersion}
        />

        {hasHistory && (
          <>
            <ViewDivider />
            <Button
              variant="text"
              type="button"
              onClick={() => setIsHistoryOpen(true)}
            >
              <MutedText>
                {t('VIEW_HISTORY')} <ClockCircleOutlined />
              </MutedText>
            </Button>

            <PayRateHistoryDrawer
              cardId={cardId}
              cardName={cardTitle}
              open={isHistoryOpen}
              onClose={() => setIsHistoryOpen(false)}
            />
          </>
        )}
      </ViewFormCard>
    )
  }

  return (
    <ViewFormCard
      level={2}
      title={previousVersion ? previousVersion.name : cardTitle}
      rightChildren={
        <EditActions>
          {cardId && originalCard && (
            <Button
              variant="text"
              type="button"
              icon={
                <CloseOutlined style={{ color: theme.color.neutral.color4 }} />
              }
              onClick={handleCancel}
            >
              {previousVersion ? (
                <MutedText>{t('CANCEL_UPDATES')}</MutedText>
              ) : (
                <MutedText>{t('CANCEL')}</MutedText>
              )}
            </Button>
          )}
          <Button
            variant="text"
            type="button"
            icon={<Trash color={theme.color.neutral.color4} />}
            onClick={handleDelete}
          >
            <MutedText>{t('DELETE')}</MutedText>
          </Button>
        </EditActions>
      }
    >
      {prevSummary && (
        <>
          <PayRateCardSummary
            effectiveFrom={prevSummary.effectiveFrom}
            baseRate={prevSummary.baseRate}
            overrides={prevSummary.overrides}
            teamMembers={previousVersion?.teamMembers}
            showDividerAfter
          />
          <PlannedUpdateHeading level={4}>
            {t('PLANNED_RATE_UPDATE')}
          </PlannedUpdateHeading>
        </>
      )}

      {originalSummary && (
        <PayRateCardSummary
          effectiveFrom={originalSummary.effectiveFrom}
          baseRate={originalSummary.baseRate}
          overrides={originalSummary.overrides}
          teamMembers={originalCard.teamMembers}
          showDividerAfter
        />
      )}

      <AntdForm.Item name={[fieldName, '_id']} noStyle>
        <input type="hidden" />
      </AntdForm.Item>

      <FormItem
        name={[fieldName, 'name']}
        label={<Text>{t('PAY_RATE_NAME')}</Text>}
        rules={[getRequiredRule(t('PAY_RATE_NAME')), getIsNameDifferentRule()]}
      >
        <Input maxLength={80} />
      </FormItem>

      <FormItem
        name={[fieldName, 'effectiveDate']}
        label={<Text>{t('EFFECTIVE_DATE')}</Text>}
        rules={[getRequiredRule(t('EFFECTIVE_DATE'))]}
      >
        <DatePicker
          format={DATE_FORMAT}
          {...(!isNewCard ? { disabledDate: disabledPastDate } : {})}
          onChange={(date) => {
            const currentTime = form.getFieldValue([
              'payRates',
              fieldName,
              'effectiveTime'
            ])
            if (date && !date.isSame(dayjs(), 'day') && !currentTime) {
              form.setFieldValue(
                ['payRates', fieldName, 'effectiveTime'],
                dayjs().startOf('day')
              )
            }
          }}
        />
      </FormItem>

      <FormItem
        name={[fieldName, 'effectiveTime']}
        label={<Text>{t('EFFECTIVE_TIME')}</Text>}
        rules={[getRequiredRule(t('EFFECTIVE_TIME'))]}
      >
        <TimePicker
          format={TIME_FORMAT}
          needConfirm={false}
          {...(!isNewCard
            ? {
                disabledTime: () =>
                  getDisabledPastTime(
                    form.getFieldValue(['payRates', fieldName, 'effectiveDate'])
                  )
              }
            : {})}
        />
      </FormItem>

      <BaseRateRow>
        <FormItem
          name={[fieldName, 'rates', 0, 'rate']}
          label={<Text>{t('BASE_RATE')}</Text>}
          rules={[getRequiredRule(t('BASE_RATE'))]}
        >
          <InputNumber
            min={0}
            max={99.99}
            precision={2}
            addonAfter={
              <FormItem name={[fieldName, 'rates', 0, 'rateType']} noStyle>
                <RateTypeSelect options={rateTypeOptions} />
              </FormItem>
            }
            style={{ width: 260 }}
          />
        </FormItem>
        <AntdForm.List name={[fieldName, 'rates']}>
          {(fields, { add: addRate }) => {
            const overrideFields = fields
              .filter((field) => field.name !== 0)
              .filter((field) => filterDeletedRates(field.name))

            return (
              <>
                <Dropdown
                  menu={{
                    items: availableOptions.map((option) => ({
                      key: option.value,
                      label: option.label
                    })),
                    onClick: ({ key }) => {
                      onDirty()
                      addRate({
                        level: shiftTypeIds.has(key)
                          ? OverrideLevel.SHIFT_TYPE
                          : OverrideLevel.VISIT_TYPE,
                        eventTypeId: key,
                        rate: undefined,
                        rateType: RateType.HOURLY
                      })
                    }
                  }}
                  disabled={availableOptions.length === 0}
                >
                  <Button type="button" variant="text" icon={<Add />}>
                    {t('ADD_OVERRIDE')}
                  </Button>
                </Dropdown>

                {overrideFields.length > 0 && (
                  <OverridesSection>
                    <OverridesDivider />
                    <OverridesHeading>{t('OVERRIDES')}</OverridesHeading>
                    {overrideFields.map((overrideField) => {
                      const eventTypeId = form.getFieldValue([
                        'payRates',
                        fieldName,
                        'rates',
                        overrideField.name,
                        'eventTypeId'
                      ])
                      const eventTypeName =
                        eventTypeNameMap[eventTypeId] || eventTypeId

                      return (
                        <OverrideRow key={overrideField.key}>
                          <FormItem
                            name={[overrideField.name, 'rate']}
                            label={<Text>{eventTypeName}</Text>}
                            rules={[getRequiredRule(eventTypeName)]}
                          >
                            <InputNumber
                              min={0}
                              max={999.99}
                              precision={2}
                              addonAfter={
                                <FormItem
                                  name={[overrideField.name, 'rateType']}
                                  noStyle
                                >
                                  <RateTypeSelect options={rateTypeOptions} />
                                </FormItem>
                              }
                              style={{ width: 260 }}
                            />
                          </FormItem>
                          <Button
                            variant="text"
                            type="button"
                            icon={<Trash color={theme.color.neutral.color4} />}
                            onClick={() => handleDeleteRate(overrideField.name)}
                          >
                            <MutedText>{t('REMOVE')}</MutedText>
                          </Button>
                        </OverrideRow>
                      )
                    })}
                  </OverridesSection>
                )}
              </>
            )
          }}
        </AntdForm.List>
      </BaseRateRow>
    </ViewFormCard>
  )
}

const EditActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ViewFormCard = styled(FormCard)`
  > div:last-child {
    gap: 16px;
  }
`

const ViewDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.neutral.color8};
  width: 100%;
`

const PlannedUpdateHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const MutedText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 500;
`

const BaseRateRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  > *:first-child {
    flex: none;
  }
`

const OverridesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const OverridesDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.neutral.color8};
  width: 100%;
`

const OverridesHeading = styled(Text)`
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const OverrideRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .ant-form-item {
    flex: none;
  }
`

const RateTypeSelect = styled(AntdSelect)`
  &.ant-select {
    width: 120px;
  }
`

export default PayRateCard
